import { InternalOptions, Options } from 'define/types'
import { openPreview } from 'ui/preview/preview'
import { Elements } from 'ui/widget/elements'
import { WidgetListeners } from 'ui/widget/listeners'
import { SyncController } from 'ui/widget/sync'
import { Destructors } from 'util/destructors'
import { Listener } from 'util/listener'
import { addMessageListener, postMessage } from 'util/message'
import { omitNil, omitUndefined } from 'util/object'
import { attachResize } from 'util/resize'
import { clearStorage, storage } from 'util/storage'
import { getLocationHash } from 'util/tg'

function collectMeta(): object {
  return {
    location: location.href,
    hash: getLocationHash(),
  }
}

class WidgetControl {
  private elements = new Elements()
  private destructors = new Destructors()
  private listeners = new WidgetListeners()
  private sync = new SyncController()

  startup() {
    window.showoff ??= {}
    window.showoff.setup = this.setup.bind(this)
    window.showoff.open = this.open.bind(this)
    window.showoff.on = this.on.bind(this)
    window.showoff.min = this.min.bind(this)
    window.showoff.max = this.max.bind(this)
    window.showoff.close = this.close.bind(this)
    window.showoff.exit = this.exit.bind(this)
    window.showoff.destroy = this.stop.bind(this)

    const options = window.showoff.options
    if (!options) return

    if (options.preview) clearStorage()

    this.start(options)
    if (options.reopen) this.reopen()
    this.dequeue()
  }

  private start(options: Options) {
    this.sync.start(() => this._start(options))
  }

  private stop() {
    this.sync.stop(() => this._stop())
  }

  private open(options?: object): void {
    this.elements.window(this.elements.state || 'min')
    this.sync.action(() => this.post('open', options))
  }

  private on(name: string, listener: Listener): void {
    this.sync.action(() => this.listeners.on(name, listener), true)
  }

  private min(): void {
    this.sync.action(() => this.post('state', { state: 'min' }))
  }

  private max(): void {
    this.sync.action(() => this.post('state', { state: 'max' }))
  }

  private close(): void {
    this.sync.action(() => this.post('state', { state: 'none' }), true)
  }

  private exit(): void {
    this.sync.action(() => this.post('exit', {}), true)
  }

  private reopen(): void {
    if (!storage.window) return
    this.elements.window(storage.window)
    this.sync.action(() => this.post('reopen'))
  }

  private setup(options?: Options) {
    options ??= window.showoff?.config
    if (!options) throw new Error('no options')
    this.stop()
    this.start(options)
  }

  private restart(options?: Options) {
    if (!options) throw new Error('no options')
    this.stop()
    this.start(options)
  }

  private async _start(options: Options): Promise<void> {
    const internal: InternalOptions = options.internal || {}

    this.elements.window(internal.container ? 'max' : undefined)

    options = await this.enrich(options)

    const meta = collectMeta()
    this.elements.create(internal, options)
    this.elements.badge.addEventListener('click', () => this.open({}))

    this.forceWindow()

    this.listen('state', ({ state }) => this.elements.window(state))
    this.listen('count', ({ count }) => this.elements.count(count))
    this.listen('close', () => this.elements.window(undefined))
    this.listen('restart', () => this.restart(options))
    this.listen('picture', event => this.listeners.fire('picture', event))
    this.listen('frame', ({ width }) => this.elements.onFrame(width))

    await new Promise((resolve) => this.listen('init', () => resolve(0)))

    this.forceWindow()

    this.destructors.add(attachResize(this.elements.frame))
    this.listen('fullscreen', props => openPreview(props))
    this.post('setup', { options, meta })

    await new Promise((resolve) => this.listen('setup', () => resolve(0)))

    this.forceWindow()
  }

  private async enrich(options: Options): Promise<Options> {
    const host = window.showoff?.host
    if (!host) throw new Error('no host')

    options = omitUndefined(options)
    const footer = options.footer
    delete options.footer

    const { product, client, company, access, preview } = options as any
    const query = new URLSearchParams(omitNil({ product, client, company, access, preview }))
    const response = await fetch(host + '/api/config?' + query, {})
    if (!response.ok) throw new Error('no config')

    const json = await response.json()
    return omitUndefined({ ...json, ...options, footer, internal: undefined })
  }

  private _stop() {
    this.listeners.clear()
    this.destructors.destroy()
    this.elements.destroy()
  }

  private post(name: string, message?: unknown) {
    postMessage(this.elements.frame, name, message)
  }

  private listen(name: string, listener: (message: any) => void): void {
    const destructor = addMessageListener(this.elements.frame, name, listener)
    this.destructors.add(destructor)
  }

  private dequeue(): void {
    if (window.showoff?.queue) {
      for (const [method, ...args] of window.showoff.queue) {
        if (method === 'open') this.open(...args)
      }
      delete window.showoff.queue
    }
  }

  private forceWindow() {
    if (this.sync.forceShow) this.elements.window('min')
  }
}

export const widget = new WidgetControl()
